import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Home as HomeIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { getPublicRecipes, copyPublicRecipe, getPublicBookAuthors, getPublicRecipesByAuthor, PublicBookAuthor, Recipe } from '../services/recipesService';
import { useLimits } from '../hooks/useLimits';

export const RecipeExplorePage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { checkRecipeLimit, refreshLimits } = useLimits();

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [bookAuthors, setBookAuthors] = useState<PublicBookAuthor[]>([]);
  const [selectedBook, setSelectedBook] = useState<PublicBookAuthor | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [all, authorsList] = await Promise.all([getPublicRecipes(), getPublicBookAuthors()]);
        setRecipes(all.filter((r) => r.userId !== user?.uid));
        setBookAuthors(authorsList.filter((a) => a.userId !== user?.uid));
      } catch {
        setSnackbar({ open: true, message: 'Erro ao carregar receitas públicas', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const authorOf = (r: Recipe) => (r.authorName || r.sharedBy || 'Anônimo').trim();

  const categories = Array.from(
    new Set(recipes.map((r) => (r.category || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const term = search.trim().toLowerCase();
  const filtered = recipes.filter((r) => {
    const matchesTerm = !term
      || r.title.toLowerCase().includes(term)
      || authorOf(r).toLowerCase().includes(term);
    const matchesCategory = category === 'all' || (r.category || '') === category;
    return matchesTerm && matchesCategory;
  });

  const openBook = async (book: PublicBookAuthor) => {
    setCategory('all');
    if (selectedBook?.userId === book.userId) {
      setSelectedBook(null);
      setLoading(true);
      try {
        const all = await getPublicRecipes();
        setRecipes(all.filter((r) => r.userId !== user?.uid));
      } finally {
        setLoading(false);
      }
      return;
    }
    setSelectedBook(book);
    setLoading(true);
    try {
      setRecipes(await getPublicRecipesByAuthor(book.userId));
    } catch {
      setSnackbar({ open: true, message: 'Erro ao abrir o livro', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (recipe: Recipe) => {
    if (!user?.uid) return;
    const limit = await checkRecipeLimit();
    if (!limit.allowed) {
      setSnackbar({ open: true, message: limit.message || 'Limite de receitas atingido', severity: 'error' });
      return;
    }
    setCopyingId(recipe.id);
    try {
      await copyPublicRecipe(user.uid, recipe);
      await refreshLimits();
      setSnackbar({ open: true, message: `"${recipe.title}" copiada pro seu livro`, severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao copiar receita', severity: 'error' });
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <>
      <TopBar title="Receitas públicas" icon={<HomeIcon sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate('/recipes')} />
      <Box sx={{ paddingTop: '70px', paddingBottom: '120px', maxWidth: '1200px', margin: '0 auto', px: 3, minHeight: '100vh' }}>
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
          Receitas que outras pessoas publicaram. Copie qualquer uma pro seu livro.
        </Typography>

        <TextField
          fullWidth
          placeholder="Buscar receita ou pessoa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: theme.radii.medium, backgroundColor: theme.colors.card } }}
          inputProps={{ 'aria-label': 'Buscar receita ou pessoa' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.colors.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />

        {bookAuthors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 0.5 }}>
              Livros de receitas
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {bookAuthors.map((a) => {
                const isActive = selectedBook?.userId === a.userId;
                return (
                  <Box
                    key={a.userId}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    aria-label={`Livro de Receitas de ${a.name}`}
                    onClick={() => openBook(a)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openBook(a);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      minHeight: 92,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      borderRadius: theme.radii.medium,
                      backgroundColor: isActive ? theme.colors.secondary : `${theme.colors.secondary}26`,
                      color: isActive ? theme.colors.text.onSecondary : theme.colors.text.primary,
                      border: `2px solid ${isActive ? theme.colors.secondary : 'transparent'}`,
                      boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.14)' : 'none',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      outline: 'none',
                      '&:focus-visible': { borderColor: theme.colors.secondary },
                      '&:hover': { backgroundColor: isActive ? theme.colors.secondary : `${theme.colors.secondary}3d` },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, wordBreak: 'break-word' }}>
                      Livro de Receitas de {a.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {categories.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 2 }}>
            <Chip
              label="Todas"
              onClick={() => setCategory('all')}
              variant={category === 'all' ? 'filled' : 'outlined'}
              size="small"
              sx={category === 'all'
                ? { backgroundColor: theme.colors.primary, color: theme.colors.text.contrast, flexShrink: 0 }
                : { borderColor: theme.colors.border, color: theme.colors.text.secondary, flexShrink: 0 }}
            />
            {categories.map((c) => (
              <Chip
                key={c}
                label={c}
                size="small"
                onClick={() => setCategory(c)}
                variant={category === c ? 'filled' : 'outlined'}
                sx={category === c
                  ? { backgroundColor: theme.colors.primary, color: theme.colors.text.contrast, flexShrink: 0 }
                  : { borderColor: theme.colors.border, color: theme.colors.text.secondary, flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: theme.colors.primary }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: theme.colors.text.secondary }}>
            <MenuBookIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">
              {recipes.length === 0 ? 'Nenhuma receita pública ainda.' : 'Nenhuma receita encontrada.'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((recipe) => (
              <Grid key={recipe.id} size={{ xs: 12, md: 6 }}>
                <Card sx={{ display: 'flex', backgroundColor: theme.colors.card, borderRadius: theme.radii.medium, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: 88,
                      flexShrink: 0,
                      backgroundImage: recipe.imageUrl ? `url(${recipe.imageUrl})` : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <CardContent sx={{ flex: 1, minWidth: 0, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography title={recipe.title} sx={{ fontWeight: 700, color: theme.colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {recipe.title}
                    </Typography>
                    {(recipe.authorName || recipe.sharedBy) && (
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1 }}>
                        por {recipe.authorName || recipe.sharedBy}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={copyingId === recipe.id ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CopyIcon />}
                      disabled={copyingId === recipe.id}
                      onClick={() => handleCopy(recipe)}
                      sx={{ backgroundColor: theme.colors.primary, textTransform: 'none' }}
                    >
                      Copiar pro meu livro
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <BottomNavigation />
    </>
  );
};
